import {
  ActionPerformed,
  Attachment,
  LocalNotifications,
  LocalNotificationSchema,
  Schedule,
} from '@capacitor/local-notifications';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { capacitorService } from '../application/CapacitorService';

export interface LocalNotification {
  /**
   * Some parameters such smallIcon, iconColor and sound will override the values in capacitor.config.json
   */

  title: string; // Title of the notification
  body: string; // The body of the notification, shown below the title.
  largeBody?: string; // Sets a multiline text block for display in a big text notification style.
  summaryText?: string; //Used to set the summary text detail in inbox and big text notification styles. Only available for Android.
  id?: number; // The notification identifier. On Android it's a 32-bit int. So the value should be between -2147483648 and 2147483647 inclusive.
  schedule?: Schedule; // 	Schedule this notification for a later time.
  sound?: string; // Name of the audio file to play when this notification is displayed. Include the file extension with the filename. On iOS, the file should be in the app bundle. On Android, the file should be in res/raw folder. Recommended format is .wav because is supported by both iOS and Android.

  /**
   * For Icon resources,the following web helps to create icons as well as the folders.
   * You have to add it to android/app/src/res
   *
   * https://romannurik.github.io/AndroidAssetStudio/icons-notification.html#source.type=clipart&source.clipart=ac_unit&source.space.trim=1&source.space.pad=0&name=ic_stat_ac_unit
   */

  smallIcon?: string; // Set a custom status bar icon. If set, this overrides the smallIcon option from Capacitor configuration. Icons should be placed in your app's res/drawable folder. The value for this option should be the drawable resource ID, which is the filename without an extension. Only available for Android.
  largeIcon?: string; // Set a large icon for notifications (Right Part). Icons should be placed in your app's res/drawable folder. The value for this option should be the drawable resource ID, which is the filename without an extension. Only available for Android.

  iconColor?: string; // Set the color of the notification icon. Only available for Android.
  attachments?: Attachment[]; // Set attachments for this notification.
  actionTypeId?: LocalNotificationType; // Associate an action type with this notification. The id are defined in the registerActionTypes method.
  extra?: any; // Any extra data to attach to the notification
  group?: string; // The name of the group this notification belongs to. Notifications in the same group are displayed together in a stack.
  groupSummary?: boolean; // If true, this notification will be the summary for the group. Use it to group this notification with other notifications from the same group.
  ongoing?: boolean; // [Doesn't seems to work] If true, the notification can't be swiped away. Calls setOngoing() on NotificationCompat.Builder with the provided value. Only available for Android.
  autoCancel?: boolean; // If true, the notification is automatically canceled when the user clicks it in the panel. Calls setAutoCancel() on NotificationCompat.Builder with the provided value. Only available for Android.
}

/**
 * LocalNotifications DOCUMENTATION: https://capacitorjs.com/docs/apis/local-notifications
 *
 *
 *
 * Note: Channels exist only on Android. It lets the user disable channels of notifications.
 * Let's say we can have one for navigation purposes, another for updates, etc.
 * a usage example in https://www.youtube.com/watch?v=A3X0-ZgU-KI
 */
export default class RoutalLocalNotifications {
  private notifications: Record<number, () => void> = {};
  // constructor() {}

  async initLocalNotifications() {
    if (capacitorService.isNative()) {
      // Ask for notification permission
      await LocalNotifications.requestPermissions();
      console.log(`LocalNotifications are initialized Permisos -> `, await LocalNotifications.checkPermissions());
      if (await LocalNotifications.checkPermissions()) {
        await registerActionTypes();
      }
    }
  }

  async scheduleNotification(localNotification: LocalNotification, callback?: (notification: ActionPerformed) => void) {
    if (!capacitorService.isNative()) {
      return;
    }

    const scheduleResult = await LocalNotifications.schedule({
      notifications: [
        {
          id: 1, //new Date().getTime(), // random integer

          ...{
            iconColor: RoutalPalette.white,
            // smallIcon: `notification_icon`, largeIcon: `notification_icon`
            largeIcon: `res://notification_icon`, // Name of the icon inside resources
          },
          ...localNotification,
          schedule: { ...localNotification.schedule, allowWhileIdle: true },
        },
      ],
    });

    if (callback) {
      const res = await LocalNotifications.addListener(`localNotificationActionPerformed`, (notification) => {
        callback(notification);
      });
      this.notifications[scheduleResult.notifications[0].id] = res.remove;
    }
    return scheduleResult.notifications[0].id;
  }

  async scheduleListenerActionPerformed(callback?: (notification: ActionPerformed) => void) {
    if (!capacitorService.isNative()) {
      return;
    }
    LocalNotifications.addListener(`localNotificationActionPerformed`, (notification) => {
      callback && callback(notification);
    });
  }

  async scheduleListenerReceived(callback?: (notification: LocalNotificationSchema) => void) {
    if (!capacitorService.isNative()) {
      return;
    }
    LocalNotifications.addListener(`localNotificationReceived`, (notification) => {
      callback && callback(notification);
    });
  }

  // Delete operations
  async removeAllListeners() {
    if (!capacitorService.isNative()) {
      return;
    }
    LocalNotifications.removeAllListeners();
  }
  async removeListener(id: number) {
    if (!capacitorService.isNative()) {
      return;
    }
    this.notifications[id]();
  }
  async cancelAllNotifications() {
    if (!capacitorService.isNative()) {
      return;
    }
    await LocalNotifications.cancel({
      notifications: Object.keys(this.notifications).map((id) => ({ id: Number(id) })),
    });
  }

  async cancelNotification(id: number) {
    if (!capacitorService.isNative()) {
      return;
    }
    await LocalNotifications.cancel({ notifications: [{ id }] });
  }
}

export enum LocalNotificationEnum {
  ROUTE = `ROUTE`,
  PENDING_REPORTS = `PENDING_REPORTS`,
}
export type LocalNotificationType = keyof typeof LocalNotificationEnum;

export enum LocalNotificationActionEnum {
  CANCEL = `CANCEL`,
  COMPLETE = `COMPLETE`,
}

export type LocalNotificationActionType = keyof typeof LocalNotificationActionEnum;

const registerActionTypes = async () => {
  /*
   * Register "Templates" for the notifications. This is the way to define the actions that the notification can have.
   * Registering action types. In order to use them, associate the correct id in the actionTypeId property of the notification.
   */
  LocalNotifications.registerActionTypes({
    types: [
      {
        id: LocalNotificationEnum.ROUTE,
        actions: [
          {
            id: LocalNotificationActionEnum.CANCEL,
            title: `Cancel Stop`,
          },
          {
            id: LocalNotificationActionEnum.COMPLETE,
            title: `Complete Stop`,
          },
        ],
      },
    ],
  });
};

export const routalLocalNotifications = new RoutalLocalNotifications();
